import type {
  AnalyticsBrowserConfig,
  AnalyticsBrowserEnv,
  GA4EventParams,
  GA4Surface,
  GtagFunction,
  PageViewPayload,
  AnalyticsUserProperties,
} from './types';
import { buildPageViewPayload } from './pageview';
import { sanitizeGA4Params, sanitizePagePath } from './sanitize';
import type { GA4EventName } from './taxonomy';

declare global {
  interface Window {
    gtag?: GtagFunction;
    dataLayer?: unknown[];
    __tovAnalyticsLocationChangePatched?: boolean;
    __tovAnalyticsPageViewTrackers?: Set<string>;
  }
}

export function parseAnalyticsBrowserConfig(
  env: AnalyticsBrowserEnv,
  surface: GA4Surface,
): AnalyticsBrowserConfig {
  const enabled =
    env.enabled !== undefined
      ? env.enabled !== 'false' && env.enabled !== '0'
      : false;

  return {
    measurementId: env.measurementId ?? '',
    enabled: enabled && Boolean(env.measurementId),
    debugMode: env.debugMode === 'true' || env.debugMode === '1',
    surface,
    linkerDomains: [],
  };
}

export interface AnalyticsClient {
  trackPageView(payload: PageViewPayload): void;
  trackEvent(name: GA4EventName, params?: GA4EventParams): void;
  setUserProperties(props: AnalyticsUserProperties): void;
  resetAnalyticsIdentity(): void;
}

export function createAnalyticsClient(
  config: AnalyticsBrowserConfig,
): AnalyticsClient {
  function getGtag(): GtagFunction | null {
    if (typeof window === 'undefined') return null;
    return window.gtag ?? null;
  }

  function isEnabled(): boolean {
    return config.enabled && Boolean(getGtag());
  }

  return {
    trackPageView(payload: PageViewPayload) {
      const gtag = getGtag();
      if (!isEnabled() || !gtag) return;

      const safePath = payload.pagePath
        ? sanitizePagePath(payload.pagePath)
        : undefined;

      gtag('event', 'page_view', {
        page_type: payload.pageType,
        surface: payload.surface,
        locale: payload.locale,
        principal_type: payload.principalType,
        module_key: payload.moduleKey,
        ...(safePath ? { page_path: safePath } : {}),
        send_to: config.measurementId,
        ...(config.debugMode ? { debug_mode: true } : {}),
      });
    },

    trackEvent(name: GA4EventName, params: GA4EventParams = {}) {
      const gtag = getGtag();
      if (!isEnabled() || !gtag) return;

      const safeParams = sanitizeGA4Params({
        ...params,
        surface: params['surface'] ?? config.surface,
      });

      gtag('event', name, {
        ...safeParams,
        send_to: config.measurementId,
        ...(config.debugMode ? { debug_mode: true } : {}),
      });
    },

    setUserProperties(props: AnalyticsUserProperties) {
      const gtag = getGtag();
      if (!isEnabled() || !gtag) return;
      gtag('set', 'user_properties', props as Record<string, unknown>);
    },

    resetAnalyticsIdentity() {
      const gtag = getGtag();
      if (!isEnabled() || !gtag) return;
      gtag('set', 'user_properties', { app_user_id: null });
    },
  };
}

export function injectGtagScript(
  measurementId: string,
  debugMode = false,
  linkerDomains: string[] = [],
): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('gtag-script')) return;

  const script = document.createElement('script');
  script.id = 'gtag-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  script.addEventListener('load', () => {
    script.dataset['tovAnalyticsLoaded'] = 'true';
  });
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer ?? [];
  const dataLayer = window.dataLayer;
  window.gtag = function (...args: Parameters<GtagFunction>) {
    dataLayer.push(args);
  };
  if (linkerDomains.length > 0) {
    window.gtag('set', 'linker', {
      accept_incoming: true,
      domains: linkerDomains,
    });
  }
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
    ...(debugMode ? { debug_mode: true } : {}),
  });
}

function installLocationChangeTracking() {
  if (typeof window === 'undefined') return;
  if (window.__tovAnalyticsLocationChangePatched) return;

  const dispatchLocationChange = () => {
    window.dispatchEvent(new Event('tov:locationchange'));
  };

  const { pushState, replaceState } = window.history;

  window.history.pushState = function (...args) {
    pushState.apply(this, args);
    dispatchLocationChange();
  };

  window.history.replaceState = function (...args) {
    replaceState.apply(this, args);
    dispatchLocationChange();
  };

  window.addEventListener('popstate', dispatchLocationChange);
  window.addEventListener('hashchange', dispatchLocationChange);
  window.__tovAnalyticsLocationChangePatched = true;
}

function installAutoPageViewTracking(
  client: AnalyticsClient,
  config: AnalyticsBrowserConfig,
) {
  if (typeof window === 'undefined') return;

  installLocationChangeTracking();

  const trackerKey = `${config.surface}:${config.measurementId}`;
  const trackers = (window.__tovAnalyticsPageViewTrackers ??= new Set());
  if (trackers.has(trackerKey)) return;
  trackers.add(trackerKey);

  let lastTrackedPath = '';

  const trackCurrentPageView = () => {
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentPath === lastTrackedPath) {
      return;
    }
    lastTrackedPath = currentPath;
    client.trackPageView(
      buildPageViewPayload(config.surface, {
        path: window.location.pathname,
      }),
    );
  };

  trackCurrentPageView();
  window.addEventListener('tov:locationchange', trackCurrentPageView);
}

export function initAnalyticsBrowser(
  config: AnalyticsBrowserConfig,
): AnalyticsClient {
  if (config.enabled && typeof document !== 'undefined') {
    injectGtagScript(
      config.measurementId,
      config.debugMode,
      normalizeLinkerDomains(config.linkerDomains ?? []),
    );
  }
  const client = createAnalyticsClient(config);
  if (config.enabled) {
    installAutoPageViewTracking(client, config);
  }
  return client;
}

function normalizeLinkerDomains(domains: string[]): string[] {
  const normalized = new Set<string>();

  for (const domain of domains) {
    const candidate = domain.trim().toLowerCase();
    if (!candidate) continue;

    try {
      normalized.add(new URL(candidate).hostname);
    } catch {
      normalized.add(candidate);
    }
  }

  return [...normalized];
}

function waitForGtagScript(timeoutMs: number): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();

  const script = document.getElementById(
    'gtag-script',
  ) as HTMLScriptElement | null;

  if (!script || script.dataset['tovAnalyticsLoaded'] === 'true') {
    return Promise.resolve();
  }

  const gtagScript = script;

  return new Promise((resolve) => {
    const timeout = window.setTimeout(done, timeoutMs);

    function done() {
      window.clearTimeout(timeout);
      gtagScript.removeEventListener('load', done);
      gtagScript.removeEventListener('error', done);
      resolve();
    }

    gtagScript.addEventListener('load', done, { once: true });
    gtagScript.addEventListener('error', done, { once: true });
  });
}

function dispatchLinkerDecorationClick(url: string): string {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return url;
  }

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.style.display = 'none';
  anchor.setAttribute('aria-hidden', 'true');
  anchor.tabIndex = -1;

  const mountPoint = document.body ?? document.documentElement;
  mountPoint.appendChild(anchor);

  anchor.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    }),
  );

  const decoratedUrl = anchor.href;
  anchor.remove();

  return decoratedUrl;
}

export async function decorateUrlWithAnalyticsLinker(
  url: string,
  options: { timeoutMs?: number } = {},
): Promise<string> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return url;
  }

  await waitForGtagScript(options.timeoutMs ?? 750);
  await new Promise((resolve) => window.requestAnimationFrame(resolve));

  return dispatchLinkerDecorationClick(url);
}
